import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { EnvelopeIcon, PhoneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useStudioUser } from "../../hooks/useStudioUser";
import { useAuth } from "../../contexts/AuthContext";
import Notification from "../Notification";
import UpgradePrompt from "../UpgradePrompt";
import { useStudioPlan } from "../../hooks/useStudioPlan";
import DOMPurify from 'dompurify';

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  accountSid: 34,
  authToken: 64,
  fromNumber: 15,
  apiKey: 128,
  webhookUrl: 500,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'accountSid':
      // Twilio Account SID format validation (starts with AC and 32 chars total)
      const accountSidRegex = /^AC[a-zA-Z0-9]{32}$/;
      return accountSidRegex.test(sanitized);
    case 'authToken':
      // Auth token validation (alphanumeric only, 32 characters)
      const authTokenRegex = /^[a-zA-Z0-9]{32}$/;
      return authTokenRegex.test(sanitized);
    case 'fromNumber':
      // Phone number validation with country code
      const phoneRegex = /^\+[1-9]\d{10,14}$/;
      return phoneRegex.test(sanitized);
    case 'apiKey':
      // API key validation (alphanumeric and some special chars)
      const apiKeyDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !apiKeyDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'webhookUrl':
      // URL validation
      try {
        new URL(sanitized);
        return true;
      } catch {
        return false;
      }
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

// Phone number formatting helper
const formatPhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Extract last 10 digits (US phone number)
  const match = cleaned.match(/(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  
  // If format doesn't match, return original
  return phoneNumber;
};

interface Integration {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  apiKey?: string;
  domain?: string;
  webhookUrl?: string;
  clientId?: string;
  clientSecret?: string;
  hasUnsavedChanges?: boolean;
  isConfigured?: boolean;
  accountSid?: string;
  authToken?: string;
  fromNumber?: string;
}

interface IntegrationSettings {
  stripe: Integration;
  gmail: Integration;
  sms: Integration;
}

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400`;
};

function IntegrationPlanLoadingState() {
  return (
    <div className="flex justify-center items-center py-8">
      <div className="animate-pulse flex space-x-4 items-center">
        <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
        <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    </div>
  );
}

export default function IntegrationsSettings() {
  const { getAuthToken } = useAuth();
  const location = useLocation();
  const { studioUser } = useStudioUser();
  const {
    hasCapability,
    loading: planLoading,
    canUseStripeConnect,
  } = useStudioPlan();
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  const integrationSettingsInitial: IntegrationSettings = {
    stripe: {
      id: "stripe",
      name: "Stripe",
      description: "Payment processing for credit cards and ACH",
      enabled: false,
      apiKey: "",
      webhookUrl: "",
    },
    gmail: {
      id: "gmail",
      name: "Gmail",
      description: "Send emails through your Gmail account",
      enabled: false,
      isConfigured: false,
    },
    sms: {
      id: "sms",
      name: "SMS",
      description: "Send SMS messages through your Twilio account",
      enabled: false,
      isConfigured: false,
      accountSid: "",
      authToken: "",
      fromNumber: "",
    },
  };

  const [integrationSettings, setIntegrationSettings] =
    useState<IntegrationSettings>(integrationSettingsInitial);

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showSMSDisconnectModal, setShowSMSDisconnectModal] = useState(false);
  const [studio, setStudio] = useState<any>(null);
  const [connecting, setConnecting] = useState(false);

  // SMS Provider selection
  const [smsProvider, setSmsProvider] = useState<'own_twilio' | 'studio_managed'>('own_twilio');
  const [requestAreaCode, setRequestAreaCode] = useState('');
  const [requestCity, setRequestCity] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [assignedPhoneNumber, setAssignedPhoneNumber] = useState<any>(null);
  const [showCancelRentalModal, setShowCancelRentalModal] = useState(false);
  const [cancellingRental, setCancellingRental] = useState(false);
  const [billingCycleEnd, setBillingCycleEnd] = useState<string | null>(null);

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const handleIntegrationUpdate = (
    integrationId: keyof IntegrationSettings,
    field: keyof Integration,
    value: string
  ) => {
    // Enhanced input validation and sanitization for security
    let sanitizedValue = sanitizeInput(value, field as string);

    // Field-specific validation and formatting
    if (field === "accountSid") {
      // Twilio Account SID format validation (starts with AC and 32 chars total)
      sanitizedValue = sanitizedValue.replace(/[^A-Za-z0-9]/g, "");
      if (sanitizedValue.length > MAX_LENGTHS.accountSid) {
        sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS.accountSid);
      }
    } else if (field === "fromNumber") {
      // Phone number validation and formatting
      const digits = sanitizedValue.replace(/[^\d+]/g, "");
      if (digits.startsWith("+")) {
        sanitizedValue = digits;
      } else if (digits.length === 10) {
        sanitizedValue = "+1" + digits;
      } else {
        sanitizedValue = digits;
      }
      if (sanitizedValue.length > MAX_LENGTHS.fromNumber) {
        sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS.fromNumber);
      }
    } else if (field === "authToken") {
      // Auth token validation (alphanumeric only)
      sanitizedValue = sanitizedValue.replace(/[^A-Za-z0-9]/g, "");
      if (sanitizedValue.length > MAX_LENGTHS.authToken) {
        sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS.authToken);
      }
    } else if (field === "apiKey") {
      // API key validation
      if (sanitizedValue.length > MAX_LENGTHS.apiKey) {
        sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS.apiKey);
      }
    } else if (field === "webhookUrl") {
      // Webhook URL validation
      if (sanitizedValue.length > MAX_LENGTHS.webhookUrl) {
        sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS.webhookUrl);
      }
    }

    setIntegrationSettings((prev) => ({
      ...prev,
      [integrationId]: {
        ...prev[integrationId],
        [field]: sanitizedValue,
        hasUnsavedChanges: true,
      },
    }));
  };

  const handleEmailProviderSignIn = async (provider: "gmail" | "outlook") => {
    if (provider === "gmail") {
      try {
        setLoading(true);
        const authToken = getAuthToken();
        if (!authToken) {
          throw new Error("Authentication required");
        }

        // Start the OAuth flow by redirecting to our backend
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/start`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              studio_id: studioUser?.studio_id,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to start Gmail authentication");
        }

        const { authUrl } = await response.json();

        // Open the Google OAuth consent screen in a new window
        const width = 600;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        // Declare variables at function scope
        let cleanup: () => void;
        let timeoutId: NodeJS.Timeout;

        const authWindow = window.open(
          authUrl,
          "Gmail Authentication",
          `width=${width},height=${height},left=${left},top=${top},noopener=no`
        );

        // Listen for the OAuth callback with security validation
        const handleOAuthMessage = async (event: MessageEvent) => {
          // Ignore messages from Stripe or other third-party origins
          if (
            event.origin === "https://js.stripe.com" ||
            event.origin === "https://connect.stripe.com"
          ) {
            return;
          }

          // Critical security: Validate message origin
          // Accept messages from both frontend and backend origins for OAuth flow
          const validOrigins = [
            window.location.origin, // Frontend origin (http://localhost:3000)
            process.env.REACT_APP_BACKEND_URL || "http://localhost:3001", // Backend origin
          ];

          if (!validOrigins.includes(event.origin)) {
            // OAuth Security: Invalid message origin - error handling without console logging
            return;
          }

          // Validate message structure and type
          if (!event.data || typeof event.data !== "object") {
            // OAuth Security: Invalid message format - error handling without console logging
            return;
          }

          if (event.data.type === "GMAIL_AUTH_ERROR") {
            // Handle OAuth errors
            setNotification({
              show: true,
              message: event.data.error || "Gmail authentication failed",
              title: "Error",
            });
            cleanup();
            clearTimeout(timeoutId);
            return;
          }

          if (event.data.type !== "GMAIL_AUTH_SUCCESS") {
            return;
          }

          // Validate OAuth response data
          if (
            !event.data.code ||
            !event.data.state ||
            typeof event.data.code !== "string" ||
            typeof event.data.state !== "string"
          ) {
            // OAuth Security: Missing or invalid OAuth parameters - error handling without console logging
            setNotification({
              show: true,
              message: "OAuth authentication failed - invalid response",
              title: "Error",
            });
            try {
              authWindow?.close();
            } catch (e) {
              // Ignore cross-origin errors when trying to close
            }
            cleanup();
            clearTimeout(timeoutId);
            return;
          }

          try {
            // Verify OAuth response with backend for security
            const verificationResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/verify`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                  code: event.data.code,
                  state: event.data.state,
                }),
              }
            );

            if (!verificationResponse.ok) {
              const errorData = await verificationResponse.json();
              throw new Error(errorData.error || "OAuth verification failed");
            }

            const verificationResult = await verificationResponse.json();

            // Only update settings after successful backend verification
            if (verificationResult.success) {
              setIntegrationSettings((prev) => ({
                ...prev,
                gmail: {
                  ...prev.gmail,
                  enabled: true,
                  isConfigured: true,
                },
              }));

              setNotification({
                show: true,
                message: "Gmail account connected successfully!",
                title: "Success",
              });
            } else {
              throw new Error("Backend verification failed");
            }
          } catch (error) {
            // OAuth verification failed - error handling without console logging
            setNotification({
              show: true,
              message:
                error instanceof Error
                  ? error.message
                  : "Failed to verify Gmail authentication",
              title: "Error",
            });
          } finally {
            // Always cleanup regardless of success or failure
            try {
              authWindow?.close();
            } catch (e) {
              // Ignore cross-origin errors when trying to close
            }
            cleanup();
            clearTimeout(timeoutId);
          }
        };

        // Set up cleanup function
        cleanup = () => {
          window.removeEventListener("message", handleOAuthMessage);
        };

        window.addEventListener("message", handleOAuthMessage);

        // Set a timeout to clean up the listener if no response is received
        timeoutId = setTimeout(() => {
          cleanup();
          try {
            authWindow?.close();
          } catch (e) {
            // Ignore cross-origin errors when trying to close
          }
          setNotification({
            show: true,
            message: "Gmail authentication timed out. Please try again.",
            title: "Error",
          });
        }, 300000); // 5 minutes timeout

        // Instead of checking window.closed (which causes policy violations),
        // rely on the postMessage for success and timeout for cleanup
        // The popup will close itself after sending the message
      } catch (error) {
        setError("Failed to authenticate with Gmail. Please try again.");
        setNotification({
          show: true,
          message: "Failed to authenticate with Gmail",
          title: "Error",
        });
      } finally {
        setLoading(false);
      }
    } else if (provider === "outlook") {
      // Outlook implementation would go here
    }
  };

  const handleGmailDisconnect = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'disconnectGmail' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    try {
      setLoading(true);
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/disconnect`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to disconnect Gmail");
      setIntegrationSettings((prev) => ({
        ...prev,
        gmail: {
          ...prev.gmail,
          enabled: false,
          isConfigured: false,
        },
      }));
      setLastSubmissionTime(now);
      setNotification({
        show: true,
        message: "Gmail disconnected successfully.",
        title: "Success",
      });
    } catch (err) {
      logSecurityEvent('GMAIL_DISCONNECT_FAILED', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: "Failed to disconnect Gmail.",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripeConnect = async () => {
    setConnecting(true);
    setError("");
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/connect/start`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (payload?.code === "upgrade_required") {
          setNotification({
            show: true,
            title: "Upgrade required",
            message:
              payload.error ||
              "Connecting Stripe requires a paid DojoTrack plan.",
          });
          return;
        }
        throw new Error("Failed to start Stripe Connect");
      }
      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      logSecurityEvent('STRIPE_CONNECT_FAILED', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setError("Failed to start Stripe Connect.");
      setNotification({
        show: true,
        message: "Failed to start Stripe Connect",
        title: "Error",
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'disconnectStripe' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    setConnecting(true);
    setError("");
    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/api/supabase/studios/disconnect-stripe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to disconnect Stripe");

      // Refresh studio info
      const studioRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      const data = await studioRes.json();
      setStudio(data);
      setLastSubmissionTime(now);
    } catch (err) {
      logSecurityEvent('STRIPE_DISCONNECT_FAILED', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setError("Failed to disconnect Stripe.");
    } finally {
      setConnecting(false);
    }
  };

  const handleSMSSave = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveSMS' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    try {
      setLoading(true);
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      // Enhanced validation before saving
      const accountSid = integrationSettings.sms.accountSid?.trim();
      const authTokenValue = integrationSettings.sms.authToken?.trim();
      const fromNumber = integrationSettings.sms.fromNumber?.trim();

      // Validate required fields
      if (!accountSid || !authTokenValue || !fromNumber) {
        throw new Error(
          "Account SID, Auth Token, and From Number are required"
        );
      }

      // Enhanced security validation
      if (!validateInput(accountSid, 'accountSid')) {
        logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'accountSid', value: accountSid.substring(0, 10) });
        throw new Error("Invalid Account SID format. Should start with 'AC' and be 34 characters long.");
      }

      if (!validateInput(authTokenValue, 'authToken')) {
        logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'authToken', value: authTokenValue.substring(0, 10) });
        throw new Error("Invalid Auth Token format. Should be 32 characters long.");
      }

      if (!validateInput(fromNumber, 'fromNumber')) {
        logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'fromNumber', value: fromNumber.substring(0, 10) });
        throw new Error("Invalid phone number format. Should include country code (e.g., +12345678901)");
      }

      // Prepare the request body - only include fields that are provided
      const requestBody: any = {
        studio_id: studioUser?.studio_id,
      };

      // Only include fields that are not empty
      if (accountSid) {
        requestBody.accountSid = sanitizeInput(accountSid, 'accountSid');
      }
      if (authTokenValue) {
        requestBody.authToken = sanitizeInput(authTokenValue, 'authToken');
      }
      if (fromNumber) {
        requestBody.fromNumber = sanitizeInput(fromNumber, 'fromNumber');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save SMS configuration");
      }

      setIntegrationSettings((prev) => ({
        ...prev,
        sms: {
          ...prev.sms,
          enabled: true,
          isConfigured: true,
          hasUnsavedChanges: false,
          // Clear the auth token field for security after successful save
          authToken: "",
        },
      }));

      setLastSubmissionTime(now);
      setNotification({
        show: true,
        message: "SMS configuration saved successfully!",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('SMS_SAVE_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to save SMS configuration",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSMSDisconnect = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'disconnectSMS' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    try {
      setLoading(true);
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error("Authentication required");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioUser?.studio_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to disconnect SMS");
      }

      setIntegrationSettings((prev) => ({
        ...prev,
        sms: {
          ...prev.sms,
          enabled: false,
          isConfigured: false,
          accountSid: "",
          authToken: "",
          fromNumber: "",
        },
      }));

      setLastSubmissionTime(now);
      setNotification({
        show: true,
        message: "SMS disconnected successfully.",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('SMS_DISCONNECT_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: "Failed to disconnect SMS.",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };


  // Fetch billing info and open cancel modal
  const handleOpenCancelModal = async () => {
    if (!studioUser?.studio_id) return;

    try {
      const authToken = getAuthToken();
      
      // Fetch subscription info to get billing cycle end
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/sms-subscription`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.ok) {
        const subscription = await response.json();
        if (subscription && subscription.billing_cycle_end) {
          setBillingCycleEnd(subscription.billing_cycle_end);
        }
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    }

    // Open modal regardless of whether we got billing info
    setShowCancelRentalModal(true);
  };

  // Handle cancellation of rented phone number
  const handleCancelRental = async () => {
    if (!studioUser?.studio_id) return;

    setCancellingRental(true);
    try {
      const authToken = getAuthToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms/cancel-rental`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            studio_id: studioUser.studio_id,
            phone_number: assignedPhoneNumber?.phone_number,
            studio_name: studio?.name
          })
        }
      );

      if (response.ok) {
        // Clear assigned phone number
        setAssignedPhoneNumber(null);
        
        // Reset SMS provider selection
        setSmsProvider('own_twilio');
        
        // Clear integration settings
        setIntegrationSettings((prev) => ({
          ...prev,
          sms: {
            ...prev.sms,
            enabled: false,
            isConfigured: false,
            accountSid: '',
            authToken: '',
            fromNumber: '',
          },
        }));
        
        setShowCancelRentalModal(false);
        setNotification({
          show: true,
          message: 'Phone number rental cancelled successfully. You can now set up your own Twilio account.',
          title: 'Success'
        });
      } else {
        throw new Error('Failed to cancel rental');
      }
    } catch (error) {
      setNotification({
        show: true,
        message: 'Failed to cancel rental. Please contact us at info@dojotrack.com',
        title: 'Error'
      });
    } finally {
      setCancellingRental(false);
    }
  };

  // Simple email request handler for phone number requests
  const handleRequestPhoneNumber = async () => {
    if (!requestAreaCode || requestAreaCode.length !== 3) {
      setNotification({
        show: true,
        message: 'Please enter a valid 3-digit area code',
        title: 'Error'
      });
      return;
    }

    setSendingRequest(true);
    try {
      const authToken = getAuthToken();
      
      // Send email via your existing email system
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            recipients: ['info@dojotrack.com'],
            studio_id: studioUser?.studio_id,
            subject: `New SMS Phone Number Request - ${studio?.name || 'Studio'}`,
            content: `
              <h2>New Phone Number Request</h2>
              
              <p><strong>Studio:</strong> ${studio?.name || 'Unknown'}</p>
              <p><strong>Studio ID:</strong> ${studioUser?.studio_id}</p>
              <p><strong>Contact Email:</strong> ${studio?.email || 'Not provided'}</p>
              <p><strong>Contact Phone:</strong> ${studio?.phone || 'Not provided'}</p>
              <p><strong>Studio Address:</strong> ${studio?.address || 'Not provided'}</p>
              
              <p><strong>Area Code Preference:</strong> ${requestAreaCode || 'Not specified'}</p>
              <p><strong>City/Location:</strong> ${requestCity || 'Not specified'}</p>
              <p><strong>Additional Notes:</strong> ${requestNotes || 'None'}</p>
              
              <hr>
              
              <h3>Action Required:</h3>
              
              <p><strong>Step 1: Purchase a Phone Number for SMS</strong></p>
              <p style="background: #FEF2F2; border-left: 4px solid #DC2626; padding: 12px; margin: 10px 0;">
                <strong>⚠️ WARNING:</strong> Failure to register an emergency address can result in a <strong>$75 fine from Twilio</strong> per violation. This is required by E911 regulations.
              </p>
              <ol style="padding-left: 20px;">
                <li style="margin-bottom: 8px;">In Twilio, go to <strong>Manage</strong> → <strong>Buy a number</strong>.</li>
                <li style="margin-bottom: 8px;">Purchase a phone number (match the area code/location preference from this request when possible).</li>
                <li style="margin-bottom: 8px;">Go to <strong>Active Numbers</strong> → open the new number. Scroll to <strong>Add emergency address</strong> → <strong>Create an address</strong>. Fill in the details: use <strong>${studio?.name || 'Studio name from above'}</strong> for the business name and <strong>${studio?.address || 'Studio address from above'}</strong> for the address. Set the <strong>nickname</strong> to something different from the business name (Twilio requires these to differ). Save.</li>
                <li style="margin-bottom: 8px;">Go to <strong>Messaging</strong> → <strong>Services</strong> → open <strong>DojoTrack Rental Phone Numbers</strong> → <strong>Sender Pool</strong>. Choose <strong>Phone Number</strong> → <strong>Continue</strong>. Select the newly purchased number and click <strong>Add Phone Number</strong>.</li>
              </ol>
              <p>Copy the purchased number in E.164 format (e.g. <code>+1XXXXXXXXXX</code>) for the database steps below.</p>

              <p><strong>Step 2: Run Database Queries</strong></p>
              
              <p><strong>⚠️ Important:</strong> Remove all inline comments (-- text) from queries before running them in Supabase to avoid syntax errors.</p>
              
              <p>Query 1 - Insert phone number record:</p>
              <pre style="background: #1F2937; color: #F9FAFB; padding: 15px; border-radius: 6px; overflow-x: auto; font-size: 13px;">INSERT INTO studio_phone_numbers (
  studio_id,
  phone_number,
  twilio_sid,
  status,
  assigned_at,
  monthly_cost
) VALUES (
  '${studioUser?.studio_id}',
  '+1XXXXXXXXXX',  -- Replace with purchased number
  NULL,
  'active',
  NOW(),
  2.00
) RETURNING id;</pre>

              <p>Query 2 - Create subscription record:</p>
              <pre style="background: #1F2937; color: #F9FAFB; padding: 15px; border-radius: 6px; overflow-x: auto; font-size: 13px;">INSERT INTO studio_sms_subscriptions (
  studio_id,
  phone_number_id,
  status,
  billing_cycle_start,
  billing_cycle_end,
  monthly_price
) VALUES (
  '${studioUser?.studio_id}',
  'UUID_FROM_QUERY_1',  -- Paste the returned UUID
  'active',
  CURRENT_DATE,
  CURRENT_DATE + INTERVAL '1 month',
  2.00
);</pre>

              <p>Query 3 - Update SMS config:</p>
              <pre style="background: #1F2937; color: #F9FAFB; padding: 15px; border-radius: 6px; overflow-x: auto; font-size: 13px;">INSERT INTO sms_config (
  studio_id,
  provider,
  fromnumber,
  accountsid,
  authtoken
) VALUES (
  '${studioUser?.studio_id}',
  'studio_managed',
  '+1XXXXXXXXXX',  -- Same number from Query 1
  NULL,
  NULL
)
ON CONFLICT (studio_id) 
DO UPDATE SET 
  provider = 'studio_managed',
  fromnumber = EXCLUDED.fromnumber,
  accountsid = NULL,
  authtoken = NULL;</pre>

              <p><strong>Step 3: Create Stripe Subscription</strong></p>
              <ul>
                <li>Go to Stripe Dashboard → Customers</li>
                <li>Search for: ${studio?.name || 'Studio'} (${studio?.email || 'N/A'})</li>
                <li>Click customer → Add subscription</li>
                <li>Select product: "SMS Service"</li>
                <li>Verify both prices are added: $2.00/month (fixed rental) + $0.017 per message (metered)</li>
                <li>Set billing start: Today</li>
                <li>Click "Create subscription"</li>
                <li>Copy the subscription ID (sub_xxxxx)</li>
                <li><strong>⚠️ Copy the subscription item ID for "SMS Service - Varies with usage"</strong> (si_xxxxx) - NOT the fixed $2.00/month one</li>
              </ul>

              <p>Query 4 - Link Stripe subscription:</p>
              <pre style="background: #1F2937; color: #F9FAFB; padding: 15px; border-radius: 6px; overflow-x: auto; font-size: 13px;">UPDATE studio_sms_subscriptions
SET 
  stripe_subscription_id = 'sub_xxxxx',      -- Paste from Stripe
  stripe_subscription_item_id = 'si_xxxxx'   -- Paste from Stripe
WHERE studio_id = '${studioUser?.studio_id}';</pre>

              <p><strong>Step 4: Notify Customer</strong></p>
              <ul>
                <li>Send confirmation email to: ${studio?.email || 'Studio contact'}</li>
                <li>Include the assigned phone number and let them know it's active</li>
              </ul>
            `
          })
        }
      );

      if (response.ok) {
        setNotification({
          show: true,
          message: 'Request sent! We will contact you within 24 to 48 hours to set up your phone number. Please be patient.',
          title: 'Success'
        });
        setRequestAreaCode('');
        setRequestCity('');
        setRequestNotes('');
      } else {
        throw new Error('Failed to send request');
      }
    } catch (error) {
      setNotification({
        show: true,
        message: 'Failed to send request. Please make sure you have connected your account to Gmail, or email us directly at info@dojotrack.com',
        title: 'Error'
      });
    } finally {
      setSendingRequest(false);
    }
  };

  const renderIntegrationFields = (integration: Integration) => {
    if (integration.id === "stripe") {
      return (
        <>
          <div>
            <label
              htmlFor={`${integration.id}-api-key`}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              API Key
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="password"
                id={`${integration.id}-api-key`}
                value={DOMPurify.sanitize(integration.apiKey || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                onChange={(e) =>
                  handleIntegrationUpdate(
                    integration.id as keyof IntegrationSettings,
                    "apiKey",
                    e.target.value
                  )
                }
                className={getInputClassName(
                  "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                )}
                placeholder="Enter your API key"
                maxLength={MAX_LENGTHS.apiKey}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor={`${integration.id}-webhook-url`}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Webhook URL
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                id={`${integration.id}-webhook-url`}
                value={DOMPurify.sanitize(integration.webhookUrl || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                onChange={(e) =>
                  handleIntegrationUpdate(
                    integration.id as keyof IntegrationSettings,
                    "webhookUrl",
                    e.target.value
                  )
                }
                className={getInputClassName(
                  "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                )}
                placeholder="Enter your webhook URL"
                maxLength={MAX_LENGTHS.webhookUrl}
              />
            </div>
          </div>
        </>
      );
    } else if (integration.id === "gmail") {
      return (
        <>
          <div className="flex flex-col xl:flex-row xl:items-center gap-2">
            <button
              onClick={() => handleEmailProviderSignIn("gmail")}
              disabled={loading}
              className={`w-full xl:w-auto flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <EnvelopeIcon className="h-5 w-5" />
              {loading
                ? "Connecting..."
                : "Sign in with Gmail / Google Workspace"}
            </button>
            {integration.isConfigured && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-1 text-xs rounded bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-semibold border border-green-200 dark:border-green-800">
                  Connected
                </span>
                <button
                  onClick={() => setShowDisconnectModal(true)}
                  className="px-3 py-1 text-xs rounded border border-red-400 dark:border-red-600 bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-600 dark:hover:border-red-500 transition"
                  disabled={loading}
                >
                  Disconnect
                </button>
              </div>
            )}
          </div>
        </>
      );
    }
    return null;
  };

  const fetchStudioInfo = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const data = await response.json();
      setStudio(data);
    } catch (err) {
      setError("Failed to fetch studio info");
    }
  };

  useEffect(() => {
    const checkGmailStatus = async () => {
      const authToken = getAuthToken();
      if (!authToken) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/gmail/status`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to check Gmail status");
        const { isConfigured } = await response.json();
        setIntegrationSettings((prev) => ({
          ...prev,
          gmail: {
            ...prev.gmail,
            enabled: isConfigured,
            isConfigured,
          },
        }));
      } catch (err) {
        // Optionally handle error
      }
    };
    checkGmailStatus();
  }, [getAuthToken]);

  // Fetch CSRF token on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []); // Empty dependency array - only run once

  // Load existing SMS configuration
  useEffect(() => {
    const loadSMSConfig = async () => {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;
      if (!planLoading && !hasCapability('messagingSms')) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioUser.studio_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (response.ok) {
          const smsConfig = await response.json();
          
          // Check provider type and load accordingly
          if (smsConfig && smsConfig.provider === 'studio_managed') {
            // Studio is using studio-managed SMS
            setSmsProvider('studio_managed');
            
            // Set assigned phone number from sms_config
            if (smsConfig.fromnumber) {
              setAssignedPhoneNumber({
                phone_number: smsConfig.fromnumber,
                assigned_at: smsConfig.created_at,
                status: 'active'
              });
            }
            
            // DON'T set isConfigured for integrationSettings.sms
            // This prevents "Own Twilio" section from showing as connected
            setIntegrationSettings((prev) => ({
              ...prev,
              sms: {
                ...prev.sms,
                enabled: false,
                isConfigured: false,  // Keep false so "Own Twilio" doesn't show as connected
                fromNumber: smsConfig.fromnumber,
              },
            }));
          } else if (smsConfig && smsConfig.accountsid) {
            // Studio is using their own Twilio
            setSmsProvider('own_twilio');
            
            setIntegrationSettings((prev) => ({
              ...prev,
              sms: {
                ...prev.sms,
                enabled: true,
                isConfigured: true,  // This SHOULD be true for own_twilio
                accountSid: smsConfig.accountsid,
                fromNumber: smsConfig.fromnumber,
                // Note: We don't load the authToken for security reasons
                authToken: "", // Keep empty for security
              },
            }));
          }
        }
      } catch (err) {
        // Failed to load SMS config - error handling without console logging
      }
    };
    loadSMSConfig();
  }, [getAuthToken, studioUser?.studio_id, planLoading, hasCapability]);

  useEffect(() => {
    const authToken = getAuthToken();
    if (!authToken) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => setStudio(data))
      .catch(() => setError("Failed to fetch studio info"))
      .finally(() => setLoading(false));
  }, [getAuthToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeStatus = params.get("stripe");
    const gmailStatus = params.get("gmail");
    const errorMessage = params.get("message");

    if (stripeStatus === "connected") {
      setNotification({
        show: true,
        message: "Successfully connected your Stripe account!",
        title: "Success",
      });
      fetchStudioInfo();
    } else if (stripeStatus === "error") {
      setNotification({
        show: true,
        message: errorMessage || "Failed to connect Stripe account",
        title: "Error",
      });
    } else if (gmailStatus === "connected") {
      setNotification({
        show: true,
        message: "Successfully connected your Gmail account!",
        title: "Success",
      });
      fetchStudioInfo();
    } else if (gmailStatus === "error") {
      setNotification({
        show: true,
        message: errorMessage || "Failed to connect Gmail account",
        title: "Error",
      });
    }
  }, [fetchStudioInfo]);

  useEffect(() => {
    if (loading || planLoading || location.hash !== "#stripe") {
      return;
    }

    const scrollToStripe = () => {
      document.getElementById("stripe")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const frameId = window.requestAnimationFrame(scrollToStripe);
    return () => window.cancelAnimationFrame(frameId);
  }, [loading, planLoading, location.hash]);

  if (loading)
    return <div className="text-gray-600 dark:text-gray-400">Loading...</div>;
  if (error)
    return <div className="text-red-600 dark:text-red-400">{error}</div>;

  return (
    <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col gap-8">
          <div className="w-full">
            {/* Email Services */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 xl:p-6">
              <div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
                  Set Up Your Email to Send Messages
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Connect your own email so students see your messages coming
                  from your real address (e.g. sensei@yourdojo.com).
                </p>
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 p-4 xl:p-6">
                    <div className="flex items-center justify-between"></div>
                    <div className="space-y-4">
                      {planLoading ? (
                        <IntegrationPlanLoadingState />
                      ) : !hasCapability('messagingEmail') ? (
                        <UpgradePrompt capability="messagingEmail" hideTitle />
                      ) : (
                        renderIntegrationFields(integrationSettings.gmail)
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full">
            {/* SMS Services */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 xl:p-6">
              <div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
                  Set Up Your SMS to Send Messages
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Choose how you want to send SMS messages to students and leads.
                </p>

                {planLoading ? (
                  <IntegrationPlanLoadingState />
                ) : !hasCapability('messagingSms') ? (
                  <UpgradePrompt capability="messagingSms" hideTitle />
                ) : (
                  <>
                {/* Provider Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    SMS Provider
                  </label>
                  <div className="space-y-3">
                    {/* Studio Managed Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                        smsProvider === 'studio_managed' 
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                      onClick={() => setSmsProvider('studio_managed')}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          checked={smsProvider === 'studio_managed'}
                          onChange={() => setSmsProvider('studio_managed')}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              Rent Phone Number from Studio (Recommended)
                            </h4>
                            <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded font-semibold">
                              Faster Setup
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Start sending immediately. No Twilio account or A2P 10DLC approval needed.
                          </p>
                          <div className="mt-2 text-sm">
                            <p className="text-gray-700 dark:text-gray-300">
                              <strong>Pricing:</strong> $2/month + $0.017 per message sent/received.
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                              (Example: 500 messages/month = $10.50 total)
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                              $2.00 billed initially, then billed each month. The sum of all text messages are billed at the end of each billing period.
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                              ✓ No technical setup • ✓ Approval in 24 to 48 hours • ✓ No A2P 10DLC hassle
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Own Twilio Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 transition ${
                        assignedPhoneNumber 
                          ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700' 
                          : smsProvider === 'own_twilio' 
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 cursor-pointer' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
                      }`}
                      onClick={() => !assignedPhoneNumber && setSmsProvider('own_twilio')}
                    >
                      <div className="flex items-start">
                        <input
                          type="radio"
                          checked={smsProvider === 'own_twilio'}
                          onChange={() => setSmsProvider('own_twilio')}
                          disabled={!!assignedPhoneNumber}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            Use Your Own Twilio Account
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Use your Twilio credentials. Lower per-message cost but requires A2P 10DLC approval.
                          </p>
                          {assignedPhoneNumber && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 mt-2 font-medium">
                              ⚠️ Please cancel your rented phone number first to switch providers.
                            </p>
                          )}
                          <div className="mt-2 text-sm">
                            <p className="text-gray-700 dark:text-gray-300">
                              <strong>Pricing:</strong> Check <a href="" target="_blank" rel="noopener noreferrer">Twilio's pricing page</a> for the latest rates.
                            </p>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                              ⚠️ Setup time: 1-2 weeks for A2P 10DLC approval
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SMS Compliance Warning */}
                <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                        ⚠️ SMS Compliance Requirements - Your Responsibility
                      </h3>
                      <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                        <p className="font-semibold mb-2">
                          By using SMS messaging, you agree to:
                        </p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>
                            <strong>ONLY send messages to people who have explicitly opted in to receive SMS from you.</strong> Never message contacts who haven't given consent.
                          </li>
                          <li>
                            <strong>Maintain your own DO NOT CONTACT list.</strong> You are responsible for tracking and honoring all opt-out requests (STOP, UNSUBSCRIBE, etc.).
                          </li>
                          <li>
                            <strong>Immediately stop messaging anyone who requests to opt out.</strong> This is required by law (TCPA) and violations can result in fines of $500-$1,500 per message.
                          </li>
                          <li>
                            Keep records of consent for all message recipients in case of disputes or regulatory inquiries.
                          </li>
                        </ul>
                        <p className="mt-2 font-medium">
                          DojoTrack is not responsible for your compliance with SMS regulations. You bear full legal responsibility for obtaining consent and honoring opt-outs.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Studio Managed Configuration */}
                {smsProvider === 'studio_managed' && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 xl:p-6 bg-gray-50 dark:bg-gray-800/50 mb-6">
                    
                    {/* Show assigned phone number if they have one */}
                    {assignedPhoneNumber ? (
                      <>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                          Your Studio Phone Number
                        </h4>
                        
                        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <PhoneIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="ml-4 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="px-2 py-1 text-xs rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold border border-green-200 dark:border-green-700">
                                  Active
                                </span>
                              </div>
                              <p className="text-xl xl:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                                {formatPhoneNumber(assignedPhoneNumber.phone_number)}
                              </p>
                              {assignedPhoneNumber.assigned_at && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  Active since {new Date(assignedPhoneNumber.assigned_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Billing:</strong> $2/month + $0.017 per message sent/received.
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            Questions? Email us at <a href="mailto:info@dojotrack.com" className="underline">info@dojotrack.com</a>
                          </p>
                        </div>

                        <button
                          onClick={handleOpenCancelModal}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          Cancel Phone Number Rental
                        </button>
                      </>
                    ) : (
                      /* Show request form if they don't have a number */
                      <>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                          Request Your Phone Number
                        </h4>
                        
                        <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Area Code *
                        </label>
                        <input
                          type="text"
                          value={requestAreaCode}
                          onChange={(e) => setRequestAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                          className={getInputClassName("mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm")}
                          placeholder="e.g., 310, 801, 415"
                          maxLength={3}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          City/Region (Optional)
                        </label>
                        <input
                          type="text"
                          value={requestCity}
                          onChange={(e) => setRequestCity(e.target.value)}
                          className={getInputClassName("mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm")}
                          placeholder="e.g., Los Angeles, CA"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Notes (Optional)
                        </label>
                        <textarea
                          value={requestNotes}
                          onChange={(e) => setRequestNotes(e.target.value)}
                          rows={3}
                          className={getInputClassName("mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm")}
                          placeholder="Any special requirements?"
                        />
                      </div>

                      <button
                        onClick={handleRequestPhoneNumber}
                        disabled={sendingRequest || !requestAreaCode || requestAreaCode.length !== 3}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PhoneIcon className="h-4 w-4 mr-2" />
                        {sendingRequest ? 'Sending...' : 'Request Phone Number'}
                      </button>

                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        <strong>Billing:</strong> $2/month phone rental + $0.017 per message sent/received.<br/>
                        We'll contact you within 24 to 48 hours to set up your phone number. 
                        Questions? Email us at <a href="mailto:info@dojotrack.com" className="text-indigo-600 hover:text-indigo-500">info@dojotrack.com</a>
                      </p>
                    </div>
                    </>
                    )}
                  </div>
                )}

                {/* Own Twilio Configuration */}
                {smsProvider === 'own_twilio' && (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      <a
                        href="https://www.twilio.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                      >
                        Create a free Twilio account →
                      </a>
                    </p>
                    {/* <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                      Watch our step-by-step video guide to set up your Twilio SMS account.
                      <a
                        href="https://youtu.be/Hwww5gWvm8U"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 ml-1"
                      >
                        Watch tutorial →
                      </a>
                    </p> */}
                  </>
                )}

                <div className="space-y-4">
                  {smsProvider === 'own_twilio' && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 p-4 xl:p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Account SID
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            value={DOMPurify.sanitize(integrationSettings.sms.accountSid || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                            onChange={(e) =>
                              handleIntegrationUpdate(
                                "sms",
                                "accountSid",
                                e.target.value
                              )
                            }
                            className={getInputClassName(
                              "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                            )}
                            placeholder="Enter your Twilio Account SID"
                            maxLength={MAX_LENGTHS.accountSid}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Auth Token
                        </label>
                        <div className="mt-1">
                          <input
                            type="password"
                            value={DOMPurify.sanitize(integrationSettings.sms.authToken || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                            onChange={(e) =>
                              handleIntegrationUpdate(
                                "sms",
                                "authToken",
                                e.target.value
                              )
                            }
                            className={getInputClassName(
                              "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                            )}
                            placeholder={
                              integrationSettings.sms.isConfigured
                                ? "Leave blank to keep existing token"
                                : "Enter your Twilio Auth Token"
                            }
                            maxLength={MAX_LENGTHS.authToken}
                          />
                        </div>
                        {integrationSettings.sms.isConfigured && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Leave blank to keep your existing auth token. Only
                            enter a new token if you want to update it.
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          From Number
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            value={DOMPurify.sanitize(integrationSettings.sms.fromNumber || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                            onChange={(e) =>
                              handleIntegrationUpdate(
                                "sms",
                                "fromNumber",
                                e.target.value
                              )
                            }
                            className={getInputClassName(
                              "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                            )}
                            placeholder="Enter your Twilio phone number (+1234567890)"
                            maxLength={MAX_LENGTHS.fromNumber}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col xl:flex-row xl:flex-wrap xl:items-center gap-2">
                        {integrationSettings.sms.hasUnsavedChanges ? (
                          <button
                            onClick={handleSMSSave}
                            disabled={
                              loading ||
                              !integrationSettings.sms.accountSid ||
                              !integrationSettings.sms.authToken ||
                              !integrationSettings.sms.fromNumber
                            }
                            className="w-full xl:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PhoneIcon className="h-4 w-4 mr-2" />
                            {loading ? "Saving..." : "Save SMS Configuration"}
                          </button>
                        ) : integrationSettings.sms.isConfigured ? (
                          <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
                            <span className="px-2 py-1 text-xs rounded bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 font-semibold border border-green-200 dark:border-green-800">
                              Connected
                            </span>
                            <button
                              onClick={() => setShowSMSDisconnectModal(true)}
                              className="flex-1 xl:flex-none px-3 py-1 text-xs rounded border border-red-400 dark:border-red-600 bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-600 dark:hover:border-red-500 transition text-center justify-center"
                              disabled={loading}
                            >
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={handleSMSSave}
                            disabled={
                              loading ||
                              !integrationSettings.sms.accountSid ||
                              !integrationSettings.sms.authToken ||
                              !integrationSettings.sms.fromNumber
                            }
                            className="w-full xl:w-auto inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <PhoneIcon className="h-4 w-4 mr-2" />
                            {loading ? "Saving..." : "Save SMS Configuration"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  )}
                </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div id="stripe" className="w-full scroll-mt-24">
            {/* Payment Services */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
                  Payment Services
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Connect your account to collect membership payments from
                  students, and keep track of tuition metrics and data.
                </p>
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                        Important Warning
                      </h3>
                      <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                        <p>
                          <strong>
                            Do not delete any subscriptions or customers
                            directly in your Stripe account.
                          </strong>{" "}
                          This will break the integration and cause errors in
                          the app. All Stripe processes should be managed
                          through this system.
                        </p>
                        <p className="mt-2">
                          When changing memberships, the system automatically
                          handles credits for downgrades or charges for
                          upgrades. Only use Stripe directly for customer
                          refunds when necessary.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {Object.entries(integrationSettings)
                    .filter(([key]) => key === "stripe")
                    .map(([key, integration]) => (
                      <div
                        key={integration.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 p-6 transition duration-150 ease-in-out hover:shadow-md"
                      >
                        <h3 className="text-base font-medium text-gray-900 dark:text-white">
                          {integration.name}
                        </h3>
                        {integration.id === "stripe" ? (
                          planLoading ? (
                            <IntegrationPlanLoadingState />
                          ) : !canUseStripeConnect ? (
                            <UpgradePrompt capability="stripeConnect" className="mt-4" />
                          ) : (
                            <div className="mt-4 flex flex-wrap items-center gap-3">
                              {studio?.stripe_user_id ? (
                                <>
                                  <span className="px-4 py-2 rounded-md bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm font-medium border border-green-200 dark:border-green-800">
                                    Connected
                                  </span>
                                  <button
                                    onClick={handleDisconnect}
                                    disabled={connecting}
                                    className="bg-white dark:bg-gray-700 border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50"
                                  >
                                    Disconnect
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={handleStripeConnect}
                                  disabled={connecting}
                                  className="bg-white dark:bg-gray-700 border border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 px-4 py-2 rounded-md text-sm font-medium transition disabled:opacity-50"
                                >
                                  Connect with Stripe
                                </button>
                              )}
                            </div>
                          )
                        ) : null}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disconnect Confirmation Modal */}
        {showDisconnectModal && (
          <div className="fixed inset-0 z-30 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Disconnect Gmail
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to disconnect your Gmail account?
                        You will not be able to send emails until you reconnect.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-red-600 dark:border-red-500 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 sm:ml-3 sm:w-auto"
                    onClick={async () => {
                      setShowDisconnectModal(false);
                      await handleGmailDisconnect();
                    }}
                    disabled={loading}
                  >
                    Disconnect
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    onClick={() => setShowDisconnectModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SMS Disconnect Confirmation Modal */}
        {showSMSDisconnectModal && (
          <div className="fixed inset-0 z-30 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Disconnect SMS
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to disconnect your SMS
                        configuration? You will not be able to send SMS messages
                        until you reconnect.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-red-600 dark:border-red-500 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20 sm:ml-3 sm:w-auto"
                    onClick={async () => {
                      setShowSMSDisconnectModal(false);
                      await handleSMSDisconnect();
                    }}
                    disabled={loading}
                  >
                    Disconnect
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                    onClick={() => setShowSMSDisconnectModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Rental Modal */}
        {showCancelRentalModal && (
          <div className="fixed inset-0 z-30 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCancelRentalModal(false)} />
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => setShowCancelRentalModal(false)}
                    disabled={cancellingRental}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="w-full">
                  <div className="mt-3 sm:mt-0">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Cancel Phone Number Rental
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Are you sure you want to cancel your rented phone number?
                      </p>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded p-3 text-sm">
                        <p className="text-gray-700 dark:text-gray-300 mb-2">
                          <strong>What happens:</strong>
                        </p>
                        <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                          <li>Your number <strong>{assignedPhoneNumber?.phone_number ? formatPhoneNumber(assignedPhoneNumber.phone_number) : ''}</strong> will be released immediately</li>
                          <li>
                            You'll stop being charged $2/month immediately
                          </li>
                          <li>You can set up your own Twilio account after</li>
                          <li>You will be billed for any text messages sent during the billing period.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    className="w-full xl:w-auto inline-flex justify-center rounded-md bg-red-600 dark:bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 dark:hover:bg-red-600"
                    onClick={handleCancelRental}
                    disabled={cancellingRental}
                  >
                    {cancellingRental ? 'Cancelling...' : 'Cancel Rental'}
                  </button>
                  <button
                    type="button"
                    className="w-full xl:w-auto inline-flex justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => setShowCancelRentalModal(false)}
                    disabled={cancellingRental}
                  >
                    Keep Rental
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
